import React, { useState } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NestedPanel, SectionHeader, SectionLoading } from './ui/Section';
import { GhostTrendPlaceholder } from './ui/charts';
import { UI_FONT } from '../theme';
import { ResponsiveLine } from '@nivo/line';
import { usePillarComparisonData } from '../hooks/usePillarComparisonData';
import PillarComparisonSelector from './PillarComparisonSelector';
import { useFilterContext } from '../context/FilterContext';
import { format } from 'date-fns';

interface PillarComparisonProps {
  platforms: string[];
}

// Metric type for the toggle
type MetricType = 'post_volume' | 'total_engagement' | 'average_sentiment';

const PillarComparison: React.FC<PillarComparisonProps> = ({
  platforms,
}) => {
  // Get date range from FilterContext
  const { startDate, endDate } = useFilterContext();
  const theme = useTheme();
  
  // Format dates for API
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
  

  
  // State for selected brand and pillar in the dropdown
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedPillar, setSelectedPillar] = useState<string>('');
  
  // State for the active metric type to display
  const [activeMetric, setActiveMetric] = useState<MetricType>('post_volume');
  
  // Fetch data using the hook
  const { data, isLoading, error } = usePillarComparisonData({
    brand: selectedBrand,
    pillar: selectedPillar,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    platforms,
  });
  
  // Handlers for selector changes
  const handleBrandChange = (brand: string) => {
    setSelectedBrand(brand);
  };
  
  const handlePillarChange = (pillar: string) => {
    setSelectedPillar(pillar);
  };
  
  // Handle toggle change
  const handleMetricChange = (event: React.MouseEvent<HTMLElement>, newMetric: MetricType | null) => {
    if (newMetric !== null) {
      setActiveMetric(newMetric);
    }
  };
  
  // Generate an array of all dates between start and end date
  const generateDateRange = (start: string, end: string): string[] => {
    console.log(`Generating date range between ${start} and ${end}`);
    const dates: string[] = [];
    
    // Parse start and end dates using date-fns for consistency
    // We'll ensure we're working with local dates in YYYY-MM-DD format
    let currentDate = new Date(start + 'T00:00:00');  // Force midnight local time
    const endDate = new Date(end + 'T23:59:59');      // Force end of day local time
    
    // Format each date as YYYY-MM-DD and add to our range
    while (currentDate <= endDate) {
      dates.push(format(currentDate, 'yyyy-MM-dd'));
      // Add one day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log("Generated dates:", dates);
    return dates;
  };
  
  // Format data for the chart based on selected metric with complete date range
  const formatChartData = () => {
    console.log("formatChartData called with data:", data);
    console.log("startDate:", formattedStartDate, "endDate:", formattedEndDate);
    
    if (!data || !formattedStartDate || !formattedEndDate) {
      console.log("Returning empty array due to missing data or date range");
      return [];
    }
    
    if (!data.time_series_data) {
      console.error("ERROR: time_series_data is missing or undefined in the API response");
      console.log("Full data object:", data);
      return [];
    }

    // Create a map of existing date points from the API
    const dataMap: Record<string, number> = {};
    console.log("Processing time_series_data:", data.time_series_data);
    
    try {
      // Log all dates from API for debugging
      console.log("API dates:", data.time_series_data.map(point => point.date));
      
      data.time_series_data.forEach(point => {
        console.log("Processing point:", point);
        dataMap[point.date] = activeMetric === 'post_volume' 
          ? point.post_volume 
          : point.total_engagement;
      });
    } catch (err) {
      console.error("Error processing time_series_data:", err);
    }
    
    // Generate complete date range
    const dateRange = generateDateRange(formattedStartDate, formattedEndDate);
    console.log("Generated date range:", dateRange.length, "days", dateRange);
    
    // Create complete dataset with zeros for missing dates
    const completeData = dateRange.map(date => ({
      x: date,
      y: dataMap[date] || 0
    }));
    
    console.log("Complete data points:", completeData.length);
    
    // Final chart data array
    const chartData = [
      {
        id: activeMetric === 'post_volume' 
          ? 'Post Volume' 
          : activeMetric === 'total_engagement' 
            ? 'Total Engagement' 
            : 'Average Sentiment',
        data: activeMetric === 'average_sentiment' 
          ? completeData.map(d => ({ ...d, y: null }))  // Sentiment data not available yet
          : completeData
      }
    ];
    
    console.log("Final chart data:", chartData);
    return chartData;
  };
  
  // Get metric total value for display
  const getMetricTotal = () => {
    if (!data) return 0;
    
    if (activeMetric === 'post_volume') {
      return data.metrics.post_volume.toLocaleString();
    } else if (activeMetric === 'total_engagement') {
      return Math.round(data.metrics.total_engagement).toLocaleString();
    } else {
      // Average sentiment isn't implemented yet
      return null;
    }
  };
  
  const metricLabel =
    activeMetric === 'post_volume' ? 'Post volume' : activeMetric === 'total_engagement' ? 'Total engagement' : 'Average sentiment';

  return (
    <NestedPanel sx={{ display: 'flex', flexDirection: 'column', minHeight: 320 }}>
      <SectionHeader
        title="Pillar Performance"
        level="sub"
        actions={
          <ToggleButtonGroup
            value={activeMetric}
            exclusive
            onChange={handleMetricChange}
            aria-label="metric type"
            size="small"
          >
            <ToggleButton value="post_volume">Post Volume</ToggleButton>
            <ToggleButton value="total_engagement">Total Engagement</ToggleButton>
            <ToggleButton value="average_sentiment">Average Sentiment</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      {/* Selectors for brand and pillar */}
      <PillarComparisonSelector
        selectedBrand={selectedBrand}
        selectedPillar={selectedPillar}
        onBrandChange={handleBrandChange}
        onPillarChange={handlePillarChange}
      />
      
      {/* Show loading state */}
      {isLoading && <SectionLoading height={280} />}

      {/* Show error state */}
      {error && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 180 }}>
          <Typography color="error">Comparison data didn't load: {error}</Typography>
        </Box>
      )}

      {/* Show message when no brand or pillar is selected */}
      {!selectedBrand || !selectedPillar ? (
        <GhostTrendPlaceholder
          height={250}
          message={
            !selectedBrand && !selectedPillar
              ? 'Choose a brand and a pillar to see their trend'
              : !selectedBrand
                ? 'Now choose a brand'
                : 'Now choose a pillar'
          }
        />
      ) : null}
      
      {/* Show data visualization */}
      {data && !isLoading && !error && selectedBrand && selectedPillar && (
        <>
          {/* Metric summary */}
          <Box sx={{ mt: 2.5, mb: 1 }}>
            <Typography variant="h4" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {activeMetric === 'average_sentiment' ? 'Coming soon' : getMetricTotal()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {metricLabel} over the selected dates
            </Typography>
          </Box>
          
          {/* Chart */}
          <Box sx={{ height: '250px' }}>
            {activeMetric === 'average_sentiment' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">Average sentiment for this view is coming soon.</Typography>
              </Box>
            ) : !formattedStartDate || !formattedEndDate ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">Choose a date range in the filters above.</Typography>
              </Box>
            ) : (
              <ResponsiveLine
                data={formatChartData()}
                margin={{ top: 16, right: 24, bottom: 40, left: 56 }}
                xScale={{
                  type: 'time',
                  format: '%Y-%m-%d',
                  useUTC: false,
                  precision: 'day',
                }}
                xFormat="time:%Y-%m-%d"
                yScale={{
                  type: 'linear',
                  min: 0,
                  max: 'auto',
                }}
                axisBottom={{
                  format: '%b %d',
                  tickSize: 0,
                  tickPadding: 10,
                  tickRotation: 0,
                  tickValues: 5,
                  legend: '',
                  legendOffset: 40,
                  legendPosition: 'middle',
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 8,
                  tickValues: 5,
                  tickRotation: 0,
                  legend: activeMetric === 'post_volume' ? 'Post Volume' : 'Engagement',
                  legendOffset: -45,
                  legendPosition: 'middle',
                }}
                theme={{
                  text: { fontFamily: UI_FONT, fontSize: 12, fill: theme.palette.text.secondary },
                  axis: {
                    domain: { line: { stroke: theme.palette.divider, strokeWidth: 1 } },
                    ticks: { text: { fill: theme.palette.text.secondary, fontSize: 12 } },
                    legend: {
                      text: {
                        fontSize: 12,
                        fill: theme.palette.text.secondary,
                        fontWeight: 500,
                      }
                    }
                  },
                  grid: { line: { stroke: theme.palette.divider, strokeDasharray: '3 4' } },
                  crosshair: { line: { stroke: theme.palette.text.secondary, strokeOpacity: 0.4 } },
                  tooltip: {
                    container: {
                      background: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 10,
                      fontSize: 13,
                    },
                  },
                }}
                colors={[theme.palette.primary.main]}
                enableGridX={false}
                lineWidth={2}
                pointSize={5}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enablePointLabel={false}
                useMesh={true}
                curve="linear"
              />
            )}
          </Box>
        </>
      )}
    </NestedPanel>
  );
};

export default PillarComparison;